/**
 * Runs Designer actions that can be simulated inside the preview.
 *
 * This is deliberately not a firmware simulator. Only action types with a real,
 * side-effect-free preview representation are executed; every other action type is
 * ignored silently, so existing projects keep working unchanged.
 *
 * The supported actions deliberately use the same parsed action model as the
 * exporter.  Unsupported firmware commands remain no-ops in the preview.
 */
import { getH3dLightProperty } from './model3dLightWireFormat.js';
import { parseAction } from '../utils/objectActions.js';
import { getEffectiveObjectHandlers } from '../logic/pageLogic.js';
import { isLogicFlowEntry, previewLogicBranch } from './logicFlowWire.js';
import { resolveActionProperty } from './actionPropertyResolver.js';
import { parseRuntimePropertyValue, serializeRuntimePropertyValue } from './propertyValueCodec.js';
import { objectPropertyField } from '../Hasp/properties/objectPropertyStatus.js';
import {
    resolveGaugeNeedlePreviewProperty,
    setGaugeNeedlePreviewProperty,
} from '../Hasp/gaugeNeedles.js';

const CONTEXT_TOKEN = /^\{([A-Za-z_][A-Za-z0-9_]*)\}$/;

/**
 * Resolves an action value against the event context, using the same whole-value
 * `{token}` convention as the action editor. No arithmetic, no expressions.
 *
 * @returns the resolved value, or null when the token has no value in this context
 */
export function resolvePreviewActionValue(value, context) {
    const token = CONTEXT_TOKEN.exec(String(value ?? ''));
    if (!token) return value;
    const resolved = context?.[token[1]];
    return resolved === undefined ? null : resolved;
}

/**
 * Builds the `{pageId, objectId}` → canvas object resolver for a source object,
 * matching how the action editor resolves targets.
 */
export function previewTargetResolver(sourceObject) {
    const page = sourceObject?.getParent?.();
    const pageId = Number(page?.haspid);
    return (targetPageId, objectId) => {
        if (!Number.isInteger(pageId) || Number(targetPageId) !== pageId) return null;
        return (page?.getChildren?.() ?? [])
            .find(item => Number(item.haspid) === Number(objectId)) ?? null;
    };
}

/**
 * Executes one action against the preview.
 *
 * @param action a Designer action
 * @param context `{ val, resolveTarget }` — the triggering event's payload plus the
 *   target resolver; `sourceObject` is added by `dispatchPreviewActions`
 * @returns whether the action actually changed something in the preview
 */
export function executePreviewAction(action, context) {
    if (action?.type === 'templateVisibility') return executeTemplateVisibilityPreview(action, context);
    if (action?.type === 'setObjectProperty') return executeSetObjectProperty(action, context);
    if (action?.type !== 'model3dLight') return false;
    if (!getH3dLightProperty(action.property)) return false;

    const value = resolvePreviewActionValue(action.value, context);
    if (value === null || value === '') return false;

    // The controller is published by the mounted 3D preview itself, so a target that is
    // not loaded, not a 3D object, or already unmounted simply has nothing to drive.
    const target = context?.resolveTarget?.(action.target?.pageId, action.target?.objectId);
    const controller = target?.previewLightsApi;
    if (!controller?.applyLightAction) return false;

    return controller.applyLightAction({ ...action, value }) === true;
}

/**
 * Template visibility actions have a Designer-only preview counterpart. The
 * action's source object supplies the transient background page; runtime
 * serialization is still handled separately by templateVisibility.js.
 */
function executeTemplateVisibilityPreview(action, context) {
    const sourceObject = context?.sourceObject;
    const layer = context?.layer ?? sourceObject?.getLayer?.();
    const templateId = action.target?.templateId;
    if (action.operation === 'hide') {
        const state = layer?.templatePreviewState;
        if (!state?.activeTemplate || Number(state.activeTemplate.haspid) !== Number(templateId)) return false;

        // A semantic Hide closes the current Designer instance. Prefer the
        // source-derived background page when the real layer can switch pages;
        // the reset fallback keeps lightweight preview/test layers safe.
        const previewPage = state.previewPage;
        if (previewPage && typeof layer?.switchToPage === 'function') {
            layer.switchToPage(previewPage);
        } else if (typeof layer?.resetTemplatePreviewContext === 'function') {
            layer.resetTemplatePreviewContext();
        } else if (layer) {
            layer.templatePreviewState = {
                activeTemplate: null,
                sourceObject: null,
                previewPage: null,
            };
        }
        return true;
    }
    if (action.operation !== 'show') return false;
    const templatePage = (layer?.getChildren?.() ?? [])
        .find(page => page?.obj === 'template_page' && Number(page.haspid) === Number(templateId));
    const previewTemplate = context?.previewTemplate ?? layer?.previewTemplate;
    if (!templatePage || typeof previewTemplate !== 'function') return false;
    return previewTemplate.call(layer, templatePage, sourceObject) === true;
}

function previewPropertyEvent(property) {
    // This is the existing Designer notification convention: data properties
    // use data_<wire key>, text-like properties use <wire key>_text.  The
    // property type comes from the target schema; no target-property list is
    // needed here.
    return property?.metadata?.type === 'string'
        ? `${property.wireKey}_text`
        : `data_${property.wireKey}`;
}

function executeSetObjectProperty(action, context) {
    const target = context?.resolveTarget?.(action.target?.pageId, action.target?.objectId);
    const property = resolveActionProperty(target, action.property);
    if (!target || !property) return false;

    const value = resolvePreviewActionValue(action.value, context);
    if (value === null) return false;

    // Round-trip through the existing codec so preview values follow exactly
    // the same schema type/range validation as action import/export.
    const serialized = serializeRuntimePropertyValue(value, property.metadata);
    if (serialized === null) return false;
    const typedValue = parseRuntimePropertyValue(serialized, property.metadata);
    if (typedValue === null) return false;

    // Gauge needle leaves are nested collection items, not flat object
    // fields. Keep their preview path in the existing dispatcher so actions
    // and bindings use exactly the same target spelling as serialization.
    if (target.obj === 'gauge' && resolveGaugeNeedlePreviewProperty(target, property.property)) {
        const changed = setGaugeNeedlePreviewProperty(target, property.property, typedValue);
        if (!changed) return false;
        const notify = () => target.adjust?.(previewPropertyEvent(property));
        if (typeof target.withPreviewMutation === 'function') target.withPreviewMutation(notify);
        else notify();
        return true;
    }

    const field = property.property.part === null
        ? objectPropertyField(target, property.property.name)
        : null;
    // The first emulator version intentionally supports the ordinary object
    // properties produced by Designer actions. Part-scoped action commands are
    // left unsupported rather than mutating an incomplete representation.
    if (!field) return false;

    if (typeof target.setPreviewProperty === 'function') target.setPreviewProperty(field, typedValue);
    else target[field] = typedValue;

    const notify = () => target.adjust?.(previewPropertyEvent(property));
    if (typeof target.withPreviewMutation === 'function') target.withPreviewMutation(notify);
    else notify();
    return true;
}

/**
 * Runs every preview-capable action bound to `event` on `sourceObject`.
 *
 * @param sourceObject the canvas object that produced the event
 * @param event the event name, e.g. `changed`
 * @param context the event payload, e.g. `{ val: 37 }`
 * @returns the number of actions that changed the preview
 */
export function executeActions(sourceObject, event, context = {}) {
    if (!sourceObject || !event) return 0;
    const resolveTarget = context.resolveTarget ?? previewTargetResolver(sourceObject);
    // A canvas object exports its stored handlers plus its event/action nodes.
    const handlers = Array.isArray(sourceObject.actions)
        ? (typeof sourceObject.getParent === 'function' ? getEffectiveObjectHandlers(sourceObject) : sourceObject.actions)
        : [{ event, actions: sourceObject.action?.[event] }];
    let applied = 0;
    const run = entries => {
        for (const entry of entries) {
            // A condition or switch runs the branch the event payload selects.
            if (isLogicFlowEntry(entry)) {
                run(previewLogicBranch(entry, { ...context, resolveTarget }));
                continue;
            }
            const action = typeof entry === 'string'
                ? parseAction(entry, resolveTarget)
                : entry;
            if (executePreviewAction(action, { ...context, sourceObject, resolveTarget })) applied++;
        }
    };
    for (const handler of handlers) {
        if (handler?.event !== event) continue;
        run(Array.isArray(handler.actions)
            ? handler.actions
            : (handler.actions === undefined ? [] : [handler.actions]));
    }
    return applied;
}

// Backward-compatible name used by the existing Slider preview integration.
export function dispatchPreviewActions(sourceObject, event, context = {}) {
    return executeActions(sourceObject, event, context);
}
